import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Package, MapPin, ShoppingBag, ChevronUp, ChevronDown,
  RefreshCcw, Store, Download
} from 'lucide-react';
import { FaTruck, FaMapMarkerAlt, FaCheck, FaCheckCircle, FaBox, FaEye } from 'react-icons/fa';
import { orderAPI } from '../../api';
import './ViewOrders.css';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import ViewReturnOrder from '../Returns/ViewReturnOrder';
import toast from 'react-hot-toast';


interface ViewOrdersProps { 
  order: any;
  onBack: () => void;
}

const getStatusClass = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'confirmed': return 'vo-status-chip vo-chip-confirmed';
    case 'delivered': return 'vo-status-chip vo-chip-delivered';
    case 'shipped':   return 'vo-status-chip vo-chip-shipped';
    case 'pending':   return 'vo-status-chip vo-chip-pending';
    case 'cancelled': return 'vo-status-chip vo-chip-cancelled';
    case 'return_picked_up':
    case 'return picked up':
    case 'picked up': return 'vo-status-chip vo-chip-picked-up';
    case 'return_initiated':
    case 'return initiated': return 'vo-status-chip vo-chip-return-initiated';
    case 'return_completed':
    case 'return completed': return 'vo-status-chip vo-chip-return-completed';
    default:          return 'vo-status-chip vo-chip-default';
  }
};

const getInCardStatusClass = (status: string) => {
  const base = 'vo-status-chip in-card';
  switch (status?.toLowerCase()) {
    case 'confirmed':
    case 'delivered':   return `${base} delivered`;
    case 'pending':     return `${base} pending`;
    case 'cancelled':   return `${base} cancelled`;
    default:            return `${base}`;
  }
};

const ViewOrders = ({ order, onBack }: ViewOrdersProps) => {
  const [orderItems, setOrderItems] = useState<any[]>([]);
  
  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [isRefundExpanded, setIsRefundExpanded] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingSellerPdf, setDownloadingSellerPdf] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [viewReturnProductId, setViewReturnProductId] = useState<string | null>(null);

  const { seller } = useSelector((state: RootState) => state.auth);

  const getOrderItems = async () => {
    try {
      setLoading(true);
      const res = await orderAPI.getOrderDetails({ orderId: order._id });
      const data = res.data.data;
      setOrderData(data);
      setOrderItems(data.products || []);
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { getOrderItems(); }, []);

  /* ── Action Handlers (unchanged logic) ── */
  const handlePickup = async (orderId: string, productId: string, productName: string) => {
    const deliveryDays = seller?.shippingRules?.deliveryDays || 2;
    const dt = new Date();
    dt.setDate(dt.getDate() + deliveryDays);
    const defaultDate = dt.toISOString().split('T')[0];
    const minDate = new Date().toISOString().split('T')[0];

    const formattedDefaultDate = dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

    const result = await Swal.fire({
      html: `
        <div style="padding: 4px 2px; font-family: 'Inter', sans-serif;">
          <div style="width: 62px; height: 62px; background: linear-gradient(135deg, #eff6ff, #dbeafe); border: 2px solid #bfdbfe; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; box-shadow: 0 8px 16px -4px rgba(37, 99, 235, 0.15);">
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
          </div>
          <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 6px 0; letter-spacing: -0.02em;">Confirm Pickup?</h2>
          <p style="font-size: 13.5px; color: #64748b; margin: 0 0 20px 0; line-height: 1.4;">Schedule pickup and set the expected delivery date for this product.</p>

          <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 16px; margin-bottom: 20px; text-align: left;">
            <div style="font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;">Product</div>
            <div style="font-size: 14px; font-weight: 600; color: #1e293b; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;" title="${productName}">
              ${productName}
            </div>
          </div>

          <div style="text-align: left; margin-bottom: 4px;">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
              <label for="delivery-date" style="font-size: 13px; font-weight: 600; color: #334155; display: flex; align-items: center; gap: 6px;">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
                Expected Delivery Date
              </label>
              <span style="font-size: 11px; font-weight: 600; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 6px;">Within ${deliveryDays} days</span>
            </div>
            <input 
              type="date" 
              id="delivery-date" 
              class="pickup-date-input"
              min="${minDate}" 
              max="${defaultDate}" 
              value="${defaultDate}" 
              style="width: 100%; height: 44px; border-radius: 10px; border: 1.5px solid #cbd5e1; font-size: 14px; font-family: 'Inter', sans-serif; font-weight: 500; padding: 0 14px; outline: none; color: #0f172a; background: #ffffff; box-sizing: border-box; transition: all 0.2s ease;"
            />
            <div style="font-size: 12px; color: #94a3b8; margin-top: 6px;">Delivery SLA deadline: ${formattedDefaultDate}</div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Yes, Pickup',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'pickup-swal-popup',
        actions: 'pickup-swal-actions',
        confirmButton: 'pickup-swal-confirm',
        cancelButton: 'pickup-swal-cancel',
        validationMessage: 'pickup-swal-validation'
      },
      preConfirm: () => {
        const deliveryDate = (document.getElementById('delivery-date') as HTMLInputElement)?.value;
        if (!deliveryDate) {
          Swal.showValidationMessage('Please select a delivery date');
          return false;
        }
        return deliveryDate;
      }
    });

    if (!result.isConfirmed) return;
    const deliveryDate = result.value;

    try {
      setActionLoadingId(productId);
      Swal.fire({
        title: 'Updating Pickup...',
        text: 'Please wait while we update the pickup status.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: { popup: 'pickup-swal-popup' },
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const res = await orderAPI.pickupProduct({ orderId, productId, deliveryDate });
      await Swal.fire({
        icon: 'success',
        title: res.data.allPickedUp ? 'Order Ready' : 'Picked Up',
        text: res.data.allPickedUp ? 'All products are picked up successfully.' : 'Product pickup updated.',
        confirmButtonColor: '#10b981',
        customClass: { popup: 'pickup-swal-popup' }
      });
      getOrderItems();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: 'Unable to update pickup.', confirmButtonColor: '#ef4444', customClass: { popup: 'pickup-swal-popup' } });
    } finally {
      setActionLoadingId(null);
    }
  };

  const markOutForDelivery = async (orderId: string, productId: string, productName: string) => {
    const result = await Swal.fire({
      html: `
        <div style="padding: 4px 2px; font-family: 'Inter', sans-serif;">
          <div style="width: 62px; height: 62px; background: linear-gradient(135deg, #eff6ff, #dbeafe); border: 2px solid #bfdbfe; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; box-shadow: 0 8px 16px -4px rgba(37, 99, 235, 0.15);">
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="1" y="3" width="15" height="13"></rect>
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
              <circle cx="5.5" cy="18.5" r="2.5"></circle>
              <circle cx="18.5" cy="18.5" r="2.5"></circle>
            </svg>
          </div>
          <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 6px 0; letter-spacing: -0.02em;">Ready for Delivery?</h2>
          <p style="font-size: 13.5px; color: #64748b; margin: 0 0 20px 0; line-height: 1.4;">Mark this product as dispatched and ready for customer handover.</p>

          <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 16px; margin-bottom: 18px; text-align: left;">
            <div style="font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;">Product</div>
            <div style="font-size: 14px; font-weight: 600; color: #1e293b; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;" title="${productName}">
              ${productName}
            </div>
          </div>

          <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; padding: 10px 14px; font-size: 12.5px; color: #1e40af; text-align: left; display: flex; align-items: center; gap: 8px;">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            Customer will see this item is Out for Delivery.
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Yes, Ready',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'pickup-swal-popup',
        actions: 'pickup-swal-actions',
        confirmButton: 'pickup-swal-confirm-blue',
        cancelButton: 'pickup-swal-cancel'
      }
    });
    if (!result.isConfirmed) return;
    try {
      setActionLoadingId(productId);
      Swal.fire({
        title: 'Updating Status...',
        text: 'Please wait while we update the delivery status.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: { popup: 'pickup-swal-popup' },
        didOpen: () => {
          Swal.showLoading();
        }
      });
      await orderAPI.outForDelivery({ orderId, productId });
      await Swal.fire({
        icon: 'success',
        title: 'Order Updated',
        text: 'The order is now Out for Delivery.',
        confirmButtonColor: '#2563eb',
        customClass: { popup: 'pickup-swal-popup' }
      });
      getOrderItems();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: 'Unable to update status.', confirmButtonColor: '#ef4444', customClass: { popup: 'pickup-swal-popup' } });
    } finally {
      setActionLoadingId(null);
    }
  };

  const markDeliveryComplete = async (orderId: string, productId: string, productName: string) => {
    try {
      const splitRes = await orderAPI.getSettlementSplit({ orderId, productId });
      const split = splitRes.data.data;
      const isCod = split.paymentMode === 'COD';

      if (isCod) {
        const { isConfirmed } = await Swal.fire({
          html: `
            <div style="padding: 4px 2px; font-family: 'Inter', sans-serif;">
              <div style="width: 62px; height: 62px; background: linear-gradient(135deg, #dcfce7, #bbf7d0); border: 2px solid #86efac; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; box-shadow: 0 8px 16px -4px rgba(22, 163, 74, 0.2);">
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
              </div>
              <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 6px 0; letter-spacing: -0.02em;">Collect Cash</h2>
              <p style="font-size: 13.5px; color: #64748b; margin: 0 0 20px 0; line-height: 1.4;">Please collect the exact cash amount from the customer to complete delivery.</p>

              <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 16px; margin-bottom: 20px; text-align: left;">
                <div style="display: flex; justify-content: space-between; margin-bottom: 12px; border-bottom: 1px dashed #cbd5e1; padding-bottom: 12px;">
                  <span style="color: #64748b; font-size: 13px; font-weight: 500;">Product</span>
                  <span style="color: #0f172a; font-size: 13px; font-weight: 600; text-align: right; max-width: 60%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${productName}">${productName}</span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <span style="color: #0f172a; font-size: 15px; font-weight: 700;">Amount to Collect</span>
                  <span style="color: #16a34a; font-size: 24px; font-weight: 800;">₹${orderData?.totalAmount}</span>
                </div>
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: 'Verify Cash Collected',
          cancelButtonText: 'Cancel',
          buttonsStyling: false,
          customClass: {
            popup: 'pickup-swal-popup',
            actions: 'pickup-swal-actions',
            confirmButton: 'pickup-swal-confirm',
            cancelButton: 'pickup-swal-cancel'
          }
        });
        if (!isConfirmed) return;
      }

      const { value: otp } = await Swal.fire({
        html: `
          <div style="padding: 4px 2px; font-family: 'Inter', sans-serif;">
            <div style="width: 62px; height: 62px; background: linear-gradient(135deg, #ecfdf5, #d1fae5); border: 2px solid #a7f3d0; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; box-shadow: 0 8px 16px -4px rgba(16, 185, 129, 0.2);">
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                <polyline points="9 12 11 14 15 10"></polyline>
              </svg>
            </div>
            <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 6px 0; letter-spacing: -0.02em;">Verify Delivery OTP</h2>
            <p style="font-size: 13.5px; color: #64748b; margin: 0 0 20px 0; line-height: 1.4;">Enter the OTP given by the customer to confirm delivery.</p>

            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 16px; margin-bottom: 16px; text-align: left;">
              <div style="font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;">Product</div>
              <div style="font-size: 14px; font-weight: 600; color: #1e293b; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;" title="${productName}">
                ${productName}
              </div>
            </div>

            <div style="background: ${isCod ? '#f0fdf4' : '#eff6ff'}; border: 1px solid ${isCod ? '#bbf7d0' : '#bfdbfe'}; border-radius: 10px; padding: 10px 14px; font-size: 13px; color: ${isCod ? '#166534' : '#1e40af'}; margin-bottom: 16px; text-align: left; display: flex; align-items: center; gap: 8px;">
              ${isCod ? '✓ Cash collected successfully.' : '💳 Online payment verified.'}
            </div>

            <div style="text-align: left; margin-bottom: 4px;">
              <label for="delivery-otp-input" style="font-size: 13px; font-weight: 600; color: #334155; margin-bottom: 8px; display: block;">Customer OTP</label>
              <input 
                type="text" 
                id="delivery-otp-input" 
                class="pickup-date-input"
                placeholder="••••••" 
                maxlength="8"
                style="width: 100%; height: 48px; border-radius: 10px; border: 1.5px solid #cbd5e1; font-size: 20px; font-weight: 700; text-align: center; letter-spacing: 6px; outline: none; color: #0f172a; background: #ffffff; box-sizing: border-box; transition: all 0.2s ease;"
              />
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Verify & Deliver',
        cancelButtonText: 'Cancel',
        buttonsStyling: false,
        customClass: {
          popup: 'pickup-swal-popup',
          actions: 'pickup-swal-actions',
          confirmButton: 'pickup-swal-confirm',
          cancelButton: 'pickup-swal-cancel',
          validationMessage: 'pickup-swal-validation'
        },
        preConfirm: () => {
          const otpVal = (document.getElementById('delivery-otp-input') as HTMLInputElement)?.value?.trim();
          if (!otpVal) {
            Swal.showValidationMessage('OTP is required!');
            return false;
          }
          return otpVal;
        }
      });

      if (otp) {
        setActionLoadingId(productId);
        Swal.fire({
          title: 'Verifying Delivery...',
          text: 'Please wait while we complete the delivery.',
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: { popup: 'pickup-swal-popup' },
          didOpen: () => {
            Swal.showLoading();
          }
        });
        await orderAPI.verifyDeliveryOtp({ orderId, productId, otp });
        await Swal.fire({
          icon: 'success',
          title: 'Delivered',
          text: 'Order marked as delivered successfully!',
          confirmButtonColor: '#10b981',
          customClass: { popup: 'pickup-swal-popup' }
        });
        getOrderItems();
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Something went wrong. Please try again.',
        confirmButtonColor: '#ef4444',
        customClass: { popup: 'pickup-swal-popup' }
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const showReceiptModal = (productName: string, productAmount: number, adminCommission: number, gstOnComm: number, tdsAmount: number, tcsAmount: number, netSettlement: number) => {
    const dateStr = new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    Swal.fire({
      html: `
        <div style="text-align: left; font-family: monospace; background: #fff; padding: 20px; border-radius: 8px; border: 1px dashed #cbd5e1; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);">
          <div style="text-align: center; border-bottom: 2px dashed #94a3b8; padding-bottom: 10px; margin-bottom: 20px;">
            <h2 style="margin: 0; font-size: 20px; color: #0f172a;">SETTLEMENT RECEIPT</h2>
            <p style="margin: 5px 0 0; font-size: 12px; color: #64748b;">${dateStr}</p>
          </div>
          <div style="margin-bottom: 15px; font-size: 14px; color: #0f172a; font-weight: bold; text-align: center;">${productName}</div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px;">
            <span style="color: #475569;">Product Amount</span>
            <span style="font-weight: 600;">₹${productAmount.toLocaleString('en-IN')}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;">
            <span>Admin Commission</span><span>-₹${adminCommission.toLocaleString('en-IN')}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;">
            <span>GST on Comm</span><span>-₹${gstOnComm.toLocaleString('en-IN')}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;">
            <span>TDS</span><span>-₹${tdsAmount.toLocaleString('en-IN')}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 14px; color: #ef4444;">
            <span>TCS</span><span>-₹${tcsAmount.toLocaleString('en-IN')}</span>
          </div>
          <div style="border-top: 2px dashed #94a3b8; padding-top: 15px; display: flex; justify-content: space-between; font-size: 16px; font-weight: 700; color: #0f766e;">
            <span>Net Settlement</span><span>₹${netSettlement.toLocaleString('en-IN')}</span>
          </div>
          <div style="text-align: center; margin-top: 25px; font-size: 11px; color: #94a3b8;">Thank you for selling with DecsoPlus!</div>
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: 'Close',
      confirmButtonColor: '#10b981',
      width: '420px',
      customClass: { popup: 'pickup-swal-popup' }
    });
  };

  const handleSettlementDownload = async (orderId: string, productId: string) => {
    try {
      setActionLoadingId(productId + '_settlement');
      const response = await orderAPI.downloadSettlementBill(orderId, productId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Settlement_${orderId}_${productId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      console.error(err);
      toast.error('Failed to download settlement bill');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReturnAction = async (orderId: string, productId: string, productName: string, action: 'Approved' | 'Rejected' | 'Picked Up' | 'Received') => {
    const isReject = action === 'Rejected';
    const isApprove = action === 'Approved';
    const isPickup = action === 'Picked Up';

    const iconBg = isReject
      ? 'background: linear-gradient(135deg, #fef2f2, #fee2e2); border-color: #fecaca;'
      : isApprove
      ? 'background: linear-gradient(135deg, #ecfdf5, #d1fae5); border-color: #a7f3d0;'
      : isPickup
      ? 'background: linear-gradient(135deg, #f5f3ff, #ede9fe); border-color: #ddd6fe;'
      : 'background: linear-gradient(135deg, #eff6ff, #dbeafe); border-color: #bfdbfe;';

    const iconStroke = isReject ? '#ef4444' : isApprove ? '#10b981' : isPickup ? '#8b5cf6' : '#2563eb';

    const confirmBtnClass = isReject
      ? 'pickup-swal-confirm-red'
      : isApprove
      ? 'pickup-swal-confirm'
      : isPickup
      ? 'pickup-swal-confirm-purple'
      : 'pickup-swal-confirm';

    const actionDescription = isReject
      ? 'Decline the customer return request for this product.'
      : isApprove
      ? 'Approve the customer return request to proceed with return pickup.'
      : isPickup
      ? 'Confirm that the product package has been picked up from the customer.'
      : 'Confirm that the returned product has been received and verified.';

    const result = await Swal.fire({
      html: `
        <div style="padding: 4px 2px; font-family: 'Inter', sans-serif;">
          <div style="width: 62px; height: 62px; ${iconBg} border-width: 2px; border-style: solid; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.08);">
            ${isReject ? `
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            ` : isApprove ? `
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            ` : isPickup ? `
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
            ` : `
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 11 12 14 22 4"></polyline>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
              </svg>
            `}
          </div>
          <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 6px 0; letter-spacing: -0.02em;">Confirm ${action}?</h2>
          <p style="font-size: 13.5px; color: #64748b; margin: 0 0 20px 0; line-height: 1.4;">${actionDescription}</p>

          <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 16px; margin-bottom: 16px; text-align: left;">
            <div style="font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;">Product</div>
            <div style="font-size: 14px; font-weight: 600; color: #1e293b; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;" title="${productName}">
              ${productName}
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: isReject ? 'Yes, Reject' : `Yes, ${action}`,
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'pickup-swal-popup',
        actions: 'pickup-swal-actions',
        confirmButton: confirmBtnClass,
        cancelButton: 'pickup-swal-cancel'
      }
    });
    if (!result.isConfirmed) return;
    try {
      setActionLoadingId(productId);
      Swal.fire({
        title: 'Updating Return Status...',
        text: `Please wait while marking return as ${action}.`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: { popup: 'pickup-swal-popup' },
        didOpen: () => {
          Swal.showLoading();
        }
      });
      const res = await orderAPI.updateReturnStatus({ orderId, productId, returnStatus: action });
      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: res.data.message || `Return marked as ${action}`,
        confirmButtonColor: '#10b981',
        customClass: { popup: 'pickup-swal-popup' }
      });
      getOrderItems();
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Failed',
        text: err.response?.data?.message || 'Unable to update return status.',
        confirmButtonColor: '#ef4444',
        customClass: { popup: 'pickup-swal-popup' }
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const formatDate = (d: string) => {
    if (!d) return 'N/A';
    const date = new Date(d);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
      + ', '
      + date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatWarranty = (w: any) => {
    const m = parseInt(w, 10);
    if (isNaN(m) || m <= 0) return 'N/A';
    const y = Math.floor(m / 12);
    const mo = m % 12;
    const parts: string[] = [];
    if (y > 0) parts.push(`${y}yr`);
    if (mo > 0) parts.push(`${mo}mo`);
    return parts.join(' ') || 'N/A';
  };

  /* ── Seller Invoice PDF Download ── */
  const downloadSellerInvoice = async () => {
    if (!orderData) return;
    setDownloadingSellerPdf(true);
    try {
      const res = await orderAPI.downloadSellerInvoice(order._id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Seller-Invoice-${orderData.orderId || order._id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Seller invoice download failed', err);
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Unable to generate seller invoice. Please try again.',
        confirmButtonColor: '#ef4444',
        customClass: { popup: 'pickup-swal-popup' }
      });
    } finally {
      setDownloadingSellerPdf(false);
    }
  };


  /* ── Tracking stepper helpers ── */
  const getTrackingState = () => {
    const os = (orderData?.orderStatus || '').toLowerCase().trim();
    const ps = (orderData?.paymentStatus || '').toLowerCase().trim();
    const stages = ['pending', 'processing', 'pickup', 'out_for_delivery', 'completed'];
    let idx = stages.indexOf(os);
    if (idx === -1) {
      if (os === 'delivered') idx = 4;
      else if (os === 'out_for_delivery') idx = 3;
      else if (os === 'pickup') idx = 2;
      else if (os === 'processing') idx = 1;
      else idx = 0;
    }
    const isStepCompleted = (step: number) => {
      if (step === 1) return ps === 'paid' || ps === 'success' || idx >= 1;
      if (step === 2) return idx >= 1;
      if (step === 3) return idx >= 2;
      if (step === 4) return idx >= 3;
      if (step === 5) return idx >= 4;
      return false;
    };
    const getStepTime = (statuses: string[]) => {
      if (!orderData?.statusHistory) return null;
      const found = orderData.statusHistory.find((h: any) => statuses.includes(h.status?.toLowerCase()));
      if (found?.updatedAt) {
        const d = new Date(found.updatedAt);
        return `${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}, ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
      }
      return null;
    };
    const isCancelled = os === 'cancelled' || orderItems.some((item: any) => item.product?.Status === 'R');
    let progressWidth = '0%';
    if (isCancelled) progressWidth = '100%';
    else if (isStepCompleted(5)) progressWidth = '100%';
    else if (isStepCompleted(4)) progressWidth = '75%';
    else if (isStepCompleted(3)) progressWidth = '50%';
    else if (isStepCompleted(2)) progressWidth = '25%';
    return { isStepCompleted, getStepTime, isCancelled, progressWidth };
  };

  /* ══════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════ */
  if (viewReturnProductId) {
    return (
      <ViewReturnOrder
        orderId={orderData?._id || order._id}
        productId={viewReturnProductId}
        onBack={() => {
          setViewReturnProductId(null);
          getOrderItems();
        }}
      />
    );
  }

  return (
    <div className="vo-page">

      {/* ── 1. HERO HEADER ── */}
      <div className="vo-hero">
        <div className="vo-hero-left">
          <button className="vo-back-btn" onClick={onBack}>
            <ArrowLeft size={14} />
            Back
          </button>
          <div className="vo-hero-title-wrap">
            <h2 className="vo-title">Order Details</h2>
            {orderData && (
              <span className="vo-order-id">#{orderData.orderId}</span>
            )}
          </div>
        </div>
        {orderData && (
          <div className="vo-hero-right">
            <span className={getStatusClass(orderData.orderStatus)} style={{ textTransform: 'capitalize' }}>
              {orderData.orderStatus?.replace(/_/g, ' ') || 'N/A'}
            </span>
            <button
              className="vo-download-btn"
              onClick={downloadSellerInvoice}
              disabled={downloadingSellerPdf}
              title="Download Seller Invoice PDF"
            >
              {downloadingSellerPdf ? (
                <>
                  <span className="vo-btn-spinner" />
                  Generating…
                </>
              ) : (
                <>
                  <Download size={14} />
                  Seller Invoice
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className="vo-loading">
          <div className="vo-spinner" />
          Loading order details…
        </div>
      )}

      {!loading && orderData && (
        <div className="vo-body">

          {/* ── 2. ROW 1: Order Info + Shipping ── */}
          <div className="vo-grid-2">

            {/* Order Information Card */}
            <div className="vo-info-card card-order">
              <div className="vo-info-card-header">
                <div className="vo-info-card-icon vo-icon-slate">
                  <ShoppingBag size={17} />
                </div>
                <h3 className="vo-info-card-title">Order Information</h3>
              </div>
              <div className="vo-info-card-body">
                <div className="vo-field">
                  <span className="vo-field-label">Customer</span>
                  <span className="vo-field-value">{orderData.customerName || 'N/A'}</span>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Mobile</span>
                  <span className="vo-field-value">{orderData.customerMobile || 'N/A'}</span>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Total Amount</span>
                  <span className="vo-amount-value">
                    ₹{orderData.totalAmount ? Number(orderData.totalAmount).toFixed(2) : 'N/A'}
                  </span>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Status</span>
                  <span
                    className={getInCardStatusClass(orderData.orderStatus)}
                    style={{ textTransform: 'capitalize', width: 'fit-content' }}
                  >
                    {orderData.orderStatus?.replace(/_/g, ' ') || 'N/A'}
                  </span>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Date</span>
                  <span className="vo-field-value">{formatDate(orderData.createdAt)}</span>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Payment</span>
                  <span className="vo-field-value" style={{ textTransform: 'capitalize' }}>
                    {orderData.paymentMethod
                      ? (orderData.paymentMethod.toLowerCase() === 'cashfree' ? 'Online' : orderData.paymentMethod.replace(/_/g, ' '))
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Shipping Address Card */}
            {orderData.orderStatus !== 'cancelled' && (
              <div className="vo-info-card card-shipping">
                <div className="vo-info-card-header">
                  <div className="vo-info-card-icon vo-icon-teal">
                    <MapPin size={17} />
                  </div>
                  <h3 className="vo-info-card-title">Shipping Address</h3>
                </div>
                <div className="vo-info-card-body">
                  <div className="vo-field">
                    <span className="vo-field-label">Clinic</span>
                    <span className="vo-field-value">{orderData.shippingAddress?.clinicName || 'N/A'}</span>
                  </div>
                  <div className="vo-field">
                    <span className="vo-field-label">Address</span>
                    <span className="vo-field-value">{orderData.shippingAddress?.address || 'N/A'}</span>
                  </div>
                  <div className="vo-field">
                    <span className="vo-field-label">City</span>
                    <span className="vo-field-value">{orderData.shippingAddress?.city || 'N/A'}</span>
                  </div>
                  <div className="vo-field">
                    <span className="vo-field-label">State</span>
                    <span className="vo-field-value">{orderData.shippingAddress?.state || 'N/A'}</span>
                  </div>
                  <div className="vo-field">
                    <span className="vo-field-label">Zip Code</span>
                    <span className="vo-field-value">{orderData.shippingAddress?.zip || 'N/A'}</span>
                  </div>
                  <div className="vo-field">
                    <span className="vo-field-label">Country</span>
                    <span className="vo-field-value">{orderData.shippingAddress?.country || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── 3. PRODUCTS SECTION ── */}
          <div className="vo-section-title">
            <div className="vo-section-title-icon">
              <Package size={15} color="#6366f1" />
            </div>
            Products ({orderItems.length})
          </div>

          <div className="vo-products-grid">
            {orderItems.length === 0 ? (
              <p className="vo-empty">No products found.</p>
            ) : (
              orderItems.map((item: any, index: number) => {
                const pStatus = (item.product?.orderStatus || '').toLowerCase().trim();
                return (
                  <div
                    className="vo-product-card"
                    key={index}
                    data-status={pStatus || 'pending'}
                  >

                    {/* Invoice button */}
                    {(item.product?.commissionDeducted > 0 || item.product?.commission_GST_Deducted > 0) && (
                      <button
                        className="vo-product-invoice-btn"
                        style={{ position: 'absolute', top: '14px', right: '14px', zIndex: 2 }}
                        onClick={() => showReceiptModal(
                          item.product?.productName || 'Product',
                          (item.product?.sellingPrice || item.product?.price || 0) * (item.product?.quantity || 1),
                          item.product?.adminCommission?.totalCommission || item.product?.commissionDeducted || 0,
                          item.product?.Settlement?.commissionGST || item.product?.gstDeducted || 0,
                          item.product?.Settlement?.tdsAmount || 0,
                          item.product?.Settlement?.gstTcsAmount || 0,
                          item.product?.Settlement?.sellerPayable || (((item.product?.sellingPrice || item.product?.price || 0) * (item.product?.quantity || 1)) - (item.product?.commission_GST_Deducted || 0))
                        )}
                      >
                        🧾 Invoice
                      </button>
                    )}

                    {/* ── Product Top ── */}
                    <div className="vo-product-top">
                      <div className="vo-img-wrap">
                        {item.product?.image ? (
                          <img
                            src={item.product.image}
                            className="vo-img"
                            alt={item.product?.productName}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="vo-img-placeholder">
                            <Package size={30} />
                          </div>
                        )}
                      </div>

                      <div className="vo-product-info">
                        <h4>{item.product?.productName || 'N/A'}</h4>
                        <div className="vo-product-meta">
                          <span className="vo-meta-chip">
                            <span className="chip-label">Qty</span>
                            {item.product?.quantity ?? 'N/A'}
                          </span>
                          <span className="vo-meta-chip">
                            <span className="chip-label">Price</span>
                            ₹{(item.product?.sellingPrice || item.product?.price) ?? 'N/A'}
                          </span>
                          {item.adminProduct?.sku && (
                            <span className="vo-meta-chip">
                              <span className="chip-label">SKU</span>
                              {item.adminProduct.sku}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ── Seller Details ── */}
                    <div className="vo-product-sub">
                      <div className="vo-seller-header">
                        <div className="vo-seller-header-icon">
                          <Store size={11} />
                        </div>
                        <h5>Seller Details</h5>
                      </div>
                      <div className="vo-seller-grid">
                        <div className="vo-seller-item">
                          <span className="s-label">Store</span>
                          <span className="s-value">{item.seller?.store_name || 'N/A'}</span>
                        </div>
                        <div className="vo-seller-item">
                          <span className="s-label">Owner</span>
                          <span className="s-value">{item.seller?.owner_name || 'N/A'}</span>
                        </div>
                        <div className="vo-seller-item">
                          <span className="s-label">Phone</span>
                          <span className="s-value">{item.seller?.phone_number || 'N/A'}</span>
                        </div>
                        <div className="vo-seller-item">
                          <span className="s-label">Category</span>
                          <span className="s-value">{item.adminProduct?.category || 'N/A'}</span>
                        </div>
                        <div className="vo-seller-item">
                          <span className="s-label">Warranty</span>
                          <span className="s-value">{item.adminProduct?.warranty ? formatWarranty(item.adminProduct.warranty) : 'N/A'}</span>
                        </div>
                        <div className="vo-seller-item">
                          <span className="s-label">Brand</span>
                          <span className="s-value">{item.adminProduct?.brand || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {/* ── Return / Action Footer ── */}
                    {orderData.orderStatus !== 'cancelled' && (
                      <>
                        {item.product?.returnStatus ? (
                          <>
                            {/* Return banner */}
                            <div
                              className="vo-return-banner"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setViewReturnProductId(item.product.productId)}
                              title="Click to view Return Order Details"
                            >
                              <span className="vo-return-reason">
                                Return Reason: <span>{item.product?.returnReason}</span>
                              </span>
                              <span className="vo-return-badge">{item.product?.returnStatus}</span>
                            </div>
                            {/* Return action buttons */}
                            <div className="vo-product-footer">
                              <div className="vo-return-actions">
                                {item.product.returnStatus === 'Requested' && (
                                  <>
                                    <button
                                      className="pickup-primary-btn"
                                      style={{ background: '#ef4444' }}
                                      disabled={Boolean(actionLoadingId)}
                                      onClick={() => handleReturnAction(orderData._id, item.product.productId, item.product.productName, 'Rejected')}
                                    >
                                      {actionLoadingId === item.product.productId ? (
                                        <><span className="vo-btn-spinner" /> Updating…</>
                                      ) : (
                                        'Reject Return'
                                      )}
                                    </button>
                                    <button
                                      className="pickup-primary-btn"
                                      style={{ background: '#10b981' }}
                                      disabled={Boolean(actionLoadingId)}
                                      onClick={() => handleReturnAction(orderData._id, item.product.productId, item.product.productName, 'Approved')}
                                    >
                                      {actionLoadingId === item.product.productId ? (
                                        <><span className="vo-btn-spinner" /> Updating…</>
                                      ) : (
                                        'Approve Return'
                                      )}
                                    </button>
                                    <button
                                      className="pickup-primary-btn"
                                      style={{ background: '#6366f1', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                      onClick={() => setViewReturnProductId(item.product.productId)}
                                    >
                                      <FaEye size={13} /> View Return
                                    </button>
                                  </>
                                )}
                                {item.product.returnStatus === 'Approved' && (
                                  <button
                                    className="pickup-primary-btn"
                                    style={{
                                      background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                      boxShadow: '0 4px 14px rgba(139, 92, 246, 0.35)',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '8px'
                                    }}
                                    onClick={() => setViewReturnProductId(item.product.productId)}
                                  >
                                    <FaEye size={14} /> View Return Order
                                  </button>
                                )}
                                {item.product.returnStatus === 'Picked Up' && (
                                  <button
                                    className="pickup-primary-btn"
                                    style={{
                                      background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                      boxShadow: '0 4px 14px rgba(139, 92, 246, 0.35)',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '8px'
                                    }}
                                    onClick={() => setViewReturnProductId(item.product.productId)}
                                  >
                                    <FaEye size={14} /> View Return Order
                                  </button>
                                )}
                                {item.product.returnStatus === 'Received' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      Return Completed <FaCheckCircle />
                                    </span>
                                    <button
                                      className="pickup-primary-btn"
                                      style={{
                                        background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                        fontSize: '12.5px',
                                        padding: '7px 16px',
                                        boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                      }}
                                      onClick={() => setViewReturnProductId(item.product.productId)}
                                    >
                                      <FaEye size={13} /> View Return Order
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="vo-product-footer" style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <button
                              className="pickup-primary-btn"
                              style={{ background: '#f59e0b', color: '#fff' }}
                              disabled={actionLoadingId === item.product.productId + '_settlement'}
                              onClick={() => handleSettlementDownload(orderData._id, item.product.productId)}
                            >
                              {actionLoadingId === item.product.productId + '_settlement' ? (
                                <><span className="vo-btn-spinner" /> ...</>
                              ) : (
                                '🧾 Settlement'
                              )}
                            </button>
                            {['delivered', 'shipped', 'completed'].includes(pStatus) ? (
                              <button className="pickup-success-btn" disabled>
                                <FaCheck /> Delivered
                              </button>
                            ) : pStatus === 'out_for_delivery' ? (
                              <button
                                className="pickup-primary-btn"
                                style={{ background: '#10b981' }}
                                disabled={Boolean(actionLoadingId)}
                                onClick={() => markDeliveryComplete(orderData._id, item.product.productId, item.product.productName)}
                              >
                                {actionLoadingId === item.product.productId ? (
                                  <><span className="vo-btn-spinner" /> Processing…</>
                                ) : (
                                  '✓ Complete Delivery'
                                )}
                              </button>
                            ) : pStatus === 'pickup' ? (
                              <button
                                className="pickup-primary-btn"
                                style={{ background: '#3b82f6' }}
                                disabled={Boolean(actionLoadingId)}
                                onClick={() => markOutForDelivery(orderData._id, item.product.productId, item.product.productName)}
                              >
                                {actionLoadingId === item.product.productId ? (
                                  <><span className="vo-btn-spinner" /> Processing…</>
                                ) : (
                                  '🚚 Out for Delivery'
                                )}
                              </button>
                            ) : (
                              <button
                                className="pickup-primary-btn"
                                disabled={Boolean(actionLoadingId)}
                                onClick={() => handlePickup(orderData._id, item.product.productId, item.product.productName)}
                              >
                                {actionLoadingId === item.product.productId ? (
                                  <><span className="vo-btn-spinner" /> Processing…</>
                                ) : (
                                  '📦 Pickup'
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* ── 4. ORDER SUMMARY + REFUND ── */}
          {(() => {
            const pFee       = orderData.platformFee   || 0;
            const oCharges   = orderData.otherCharges  || 0;
            const sms        = orderData.smsCharges    || 0;
            const ship       = orderData.shippingFee   || 0;
            const orderTotal = Number(orderData.totalAmount) || 0;
            const itemSubtotal = orderTotal - pFee - oCharges - sms - ship;

            const totalRefunded = orderItems.reduce((acc: number, item: any) => {
              if (['Received', 'Completed', 'Refunded', 'Approved', 'Picked Up'].includes(item.product?.returnStatus)) {
                if (item.product?.returnRefundAmount != null) {
                  return acc + Number(item.product.returnRefundAmount);
                }
                if (orderItems.length === 1) {
                  return acc + orderTotal;
                }
                return acc + ((item.product?.sellingPrice || item.product?.discountPrice || item.product?.price || 0) * (item.product?.quantity || 1));
              }
              return acc;
            }, 0) || 0;

            const isPaidStatus = ['PAID', 'SUCCESS', 'COMPLETED'].includes((orderData.paymentStatus || '').toUpperCase());
            const isCancelled  = orderData.orderStatus === 'cancelled';
            const isCOD        = orderData.paymentMethod === 'cash_on_delivery';

            const displayRefund   = isCancelled && isPaidStatus && !isCOD ? Math.max(0, orderTotal - ship) : totalRefunded;
            const isUnpaidOnline  = !isCOD && !isPaidStatus;

            return (
              <div className="vo-grid-2">

                {/* Order Summary Card */}
                <div className="vo-summary-card">
                  <div className="vo-summary-card-header">
                    <div className="sc-icon">
                      <ShoppingBag size={16} />
                    </div>
                    <h3>Order Summary</h3>
                  </div>

                  <div className="vo-summary-body">
                    <div className="vo-summary-row">
                      <span className="sr-label">Order Total</span>
                      <span className="sr-value">₹{orderTotal.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="vo-summary-row">
                      <span className="sr-label">Payment Method</span>
                      <span className="sr-value" style={{ textTransform: 'capitalize' }}>
                        {isCOD ? 'Cash on Delivery' : 'Online'}
                      </span>
                    </div>
                    <div className="vo-divider" />

                    {/* Expandable breakdown */}
                    <button
                      className="vo-breakdown-btn"
                      onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                    >
                      <span>View Price Breakdown</span>
                      {isSummaryExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </button>

                    {isSummaryExpanded && (
                      <div className="vo-breakdown-items">
                        <div className="vo-breakdown-row">
                          <span className="br-label">Items Subtotal</span>
                          <span className="br-value">₹{itemSubtotal.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="vo-breakdown-row">
                          <span className="br-label">Platform Fee</span>
                          <span className="br-value">{pFee > 0 ? `₹${pFee.toLocaleString('en-IN')}` : 'Free'}</span>
                        </div>
                        <div className="vo-breakdown-row">
                          <span className="br-label">Other Charges</span>
                          <span className="br-value">{oCharges > 0 ? `₹${oCharges.toLocaleString('en-IN')}` : 'Free'}</span>
                        </div>
                        <div className="vo-breakdown-row">
                          <span className="br-label">SMS Charges</span>
                          <span className="br-value">{sms > 0 ? `₹${sms.toLocaleString('en-IN')}` : 'Free'}</span>
                        </div>
                        <div className="vo-breakdown-row">
                          <span className="br-label">Shipping Fee</span>
                          <span className="br-value">{ship > 0 ? `₹${ship.toLocaleString('en-IN')}` : 'Free'}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Total Paid */}
                  {!isCancelled && (
                    <div className="vo-total-row">
                      <span className="vo-total-label">
                        {isCOD && orderData.orderStatus?.toLowerCase() !== 'delivered' ? 'Paid' : 'Total Paid'}
                      </span>
                      <span className={`vo-total-value ${isUnpaidOnline ? 'unpaid' : ''}`}>
                        {isUnpaidOnline ? 'Unpaid' : `₹${orderTotal.toLocaleString('en-IN')}`}
                      </span>
                    </div>
                  )}
                </div>

              </div>
            );
          })()}

          {/* ── 5. TRACK ORDER ── */}
          <div className="vo-track-card">
            <div className="vo-track-header">
              <div className="vo-track-icon">
                <FaTruck size={17} />
              </div>
              <h3>Track Order</h3>
            </div>

            <div className="vo-track-body">
              <div className="track-stepper">
                {(() => {
                  const { isStepCompleted, getStepTime, isCancelled, progressWidth } = getTrackingState();

                  return (
                    <>
                      {/* Progress bar */}
                      <div className="track-stepper-progress" style={{ width: progressWidth }} />

                      {/* Step 1: Order Placed */}
                      <div className={`track-step ${isStepCompleted(1) ? 'completed' : ''}`}>
                        <div className="step-icon">
                          <FaCheckCircle size={19} color={isStepCompleted(1) ? '#0d9488' : '#cbd5e1'} />
                        </div>
                        <div className="step-content">
                          <h4>Order Placed</h4>
                          <p>Your order has been confirmed</p>
                          {isStepCompleted(1) && (
                            <p className="step-time">
                              {getStepTime(['pending', 'placed']) || (orderData?.createdAt
                                ? `${new Date(orderData.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}, ${new Date(orderData.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`
                                : '')}
                            </p>
                          )}
                        </div>
                      </div>

                      {!isCancelled && (
                        <>
                          {/* Step 2: Processing */}
                          <div className={`track-step ${isStepCompleted(2) ? 'completed' : ''}`}>
                            <div className="step-icon">
                              <FaCheck size={17} color={isStepCompleted(2) ? '#0d9488' : '#cbd5e1'} />
                            </div>
                            <div className="step-content">
                              <h4>Processing</h4>
                              <p>Payment received, order is being processed</p>
                              {isStepCompleted(2) && <p className="step-time">{getStepTime(['processing', 'packed', 'confirmed'])}</p>}
                            </div>
                          </div>

                          {/* Step 3: Pickup */}
                          <div className={`track-step ${isStepCompleted(3) ? 'completed' : ''}`}>
                            <div className="step-icon">
                              <FaBox size={17} color={isStepCompleted(3) ? '#0d9488' : '#cbd5e1'} />
                            </div>
                            <div className="step-content">
                              <h4>Pickup</h4>
                              <p>Order picked up for shipping</p>
                              {isStepCompleted(3) && <p className="step-time">{getStepTime(['pickup'])}</p>}
                            </div>
                          </div>

                          {/* Step 4: Out for Delivery */}
                          <div className={`track-step ${isStepCompleted(4) ? 'completed' : ''}`}>
                            <div className="step-icon">
                              <FaTruck size={17} color={isStepCompleted(4) ? '#0d9488' : '#cbd5e1'} />
                            </div>
                            <div className="step-content">
                              <h4>Out for Delivery</h4>
                              <p>Your order is out for delivery</p>
                              {isStepCompleted(4) && <p className="step-time">{getStepTime(['out_for_delivery'])}</p>}
                            </div>
                          </div>
                        </>
                      )}

                      {/* Step 5: Completed / Cancelled */}
                      <div className={`track-step ${isStepCompleted(5) || isCancelled ? (isCancelled ? 'cancelled' : 'completed') : ''}`}>
                        <div className="step-icon">
                          {isCancelled
                            ? <FaCheckCircle size={19} color="#dc2626" />
                            : <FaMapMarkerAlt size={19} color={isStepCompleted(5) ? '#0d9488' : '#cbd5e1'} />
                          }
                        </div>
                        <div className="step-content">
                          {isCancelled ? (
                            <>
                              <h4>Cancelled</h4>
                              <p>This order has been cancelled</p>
                              <p className="step-time" style={{ color: '#ef4444' }}>{getStepTime(['cancelled'])}</p>
                            </>
                          ) : (
                            <>
                              <h4>Completed</h4>
                              <p>Your order has been delivered successfully</p>
                              {isStepCompleted(5) && <p className="step-time">{getStepTime(['delivered', 'completed'])}</p>}
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default ViewOrders;
